import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Check,
  X,
  Search,
  Filter,
  MoreVertical,
  Upload,
  Download,
  RefreshCw,
} from 'lucide-react';
import '../styles/pages.css';

const PagesListPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all, published, drafts
  const [selectedPages, setSelectedPages] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/content/pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const seedPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/seed-pages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPages();
      }
    } catch (error) {
      console.error('Failed to seed pages:', error);
    }
  };

  const togglePublish = async (pageSlug, isPublished) => {
    try {
      const endpoint = isPublished ? 'unpublish' : 'publish';
      const response = await fetch(`${API_URL}/api/content/pages/${pageSlug}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPages();
      }
    } catch (error) {
      console.error('Failed to toggle publish:', error);
    }
  };

  const toggleEnable = async (pageSlug, isEnabled) => {
    try {
      const response = await fetch(`${API_URL}/api/content/pages/${pageSlug}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_enabled: !isEnabled })
      });
      if (response.ok) {
        fetchPages();
      }
    } catch (error) {
      console.error('Failed to toggle enable:', error);
    }
  };

  const deletePage = async (pageSlug) => {
    if (!window.confirm(`Are you sure you want to delete "${pageSlug}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/content/pages/${pageSlug}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPages();
      }
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.page_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         page.page_slug.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' ||
                         (filter === 'published' && page.is_published) ||
                         (filter === 'drafts' && !page.is_published);
    return matchesSearch && matchesFilter;
  });

  return (
    <AdminLayout>
      <div className="pages-list-page">
        {/* Header */}
        <div className="pages-header">
          <div className="header-left">
            <h2>Pages</h2>
            <span className="page-count">{pages.length} pages</span>
          </div>
          <div className="header-actions">
            <button className="btn-secondary" onClick={seedPages}>
              <RefreshCw size={16} /> Seed Default Pages
            </button>
            <button className="btn-primary" onClick={() => navigate('/admin/pages/new')}>
              <Plus size={16} /> Add Page
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="pages-filters">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="filter-tabs">
            {['all', 'published', 'drafts'].map((f) => (
              <button
                key={f}
                className={filter === f ? 'active' : ''}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Pages Table */}
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading pages...</p>
          </div>
        ) : filteredPages.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No pages found</h3>
            <p>{pages.length === 0 ? 'Get started by seeding default pages or creating a new one.' : 'Try adjusting your search or filter.'}</p>
            {pages.length === 0 && (
              <button className="btn-primary" onClick={seedPages}>
                <RefreshCw size={16} /> Seed Default Pages
              </button>
            )}
          </div>
        ) : (
          <div className="pages-table-container">
            <table className="pages-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Enabled</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPages.map((page) => (
                  <tr key={page.id}>
                    <td className="page-name-cell">
                      <FileText size={18} />
                      <span>{page.page_name}</span>
                    </td>
                    <td className="slug-cell">/{page.page_slug}</td>
                    <td>
                      <span className={`status-badge ${page.is_published ? 'published' : 'draft'}`}>
                        {page.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`toggle-btn ${page.is_enabled ? 'enabled' : 'disabled'}`}
                        onClick={() => toggleEnable(page.page_slug, page.is_enabled)}
                      >
                        {page.is_enabled ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                    </td>
                    <td className="date-cell">
                      {new Date(page.updated_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit"
                        onClick={() => navigate(`/admin/pages/${page.page_slug}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className={`action-btn ${page.is_published ? 'unpublish' : 'publish'}`}
                        onClick={() => togglePublish(page.page_slug, page.is_published)}
                        title={page.is_published ? 'Unpublish' : 'Publish'}
                      >
                        {page.is_published ? <X size={16} /> : <Check size={16} />}
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => deletePage(page.page_slug)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PagesListPage;
